import {
    math,
    ADDRESS_CLAMP_TO_EDGE,
    ASPECT_MANUAL,
    FILTER_NEAREST,
    PIXELFORMAT_RGBA8,
    PIXELFORMAT_RGBA16F,
    PIXELFORMAT_DEPTH,
    PROJECTION_ORTHOGRAPHIC,
    PROJECTION_PERSPECTIVE,
    TONEMAP_ACES,
    TONEMAP_ACES2,
    TONEMAP_FILMIC,
    TONEMAP_HEJL,
    TONEMAP_LINEAR,
    TONEMAP_NEUTRAL,
    BoundingBox,
    CameraComponent,
    Color,
    Entity,
    Mat4,
    Quat,
    Ray,
    RenderPass,
    RenderPassForward,
    RenderTarget,
    Texture,
    Vec3,
    Vec4
} from 'playcanvas';

import { PointerController } from './controllers';
import { Element, ElementType } from './element';
import { Picker } from './picker';
import { Serializer } from './serializer';
import { vertexShader, fragmentShader } from './shaders/blit-shader';
import { Splat } from './splat';
import { TweenValue } from './tween-value';
import { ShaderQuad, SimpleRenderPass } from './utils/simple-render-pass';

// work globals
const forwardVec = new Vec3();
const cameraPosition = new Vec3();
const ray = new Ray();
const vec = new Vec3();
const vecb = new Vec3();
const va = new Vec3();
const m = new Mat4();
const v4 = new Vec4();
// Face Tracker LookAt work globals (applyFaceTrackerPose)
const lookAtForward = new Vec3();
const lookAtUp = new Vec3();
const lookAtTarget = new Vec3();

// Merge Clear's mainPass clear color - same as clearPass's
const MERGED_CLEAR_COLOR = new Color(0, 0, 0, 0);

// modulo dealing with negative numbers
const mod = (n: number, m: number) => ((n % m) + m) % m;

class Camera extends Element {
    /**
     * Calculate the forward vector given azimuth and elevation angles.
     *
     * @param {Vec3} result - The Vec3 to store the result in.
     * @param {number} azim - Azimuth angle in degrees.
     * @param {number} elev - Elevation angle in degrees.
     */
    static calcForwardVec(result: Vec3, azim: number, elev: number) {
        const ex = elev * math.DEG_TO_RAD;
        const ey = azim * math.DEG_TO_RAD;
        const s1 = Math.sin(-ex);
        const c1 = Math.cos(-ex);
        const s2 = Math.sin(-ey);
        const c2 = Math.cos(-ey);
        result.set(-c1 * s2, s1, c1 * c2);
    }

    controller: PointerController;
    focalPointTween = new TweenValue({ x: 0, y: 0.5, z: 0 });
    azimElevTween = new TweenValue({ azim: 30, elev: -15 });
    distanceTween = new TweenValue({ distance: 1 });

    minElev = -90;
    maxElev = 90;

    sceneRadius = 1;

    flySpeed = 1;

    controlMode: 'orbit' | 'fly' = 'orbit';

    // during fly-mode look, stores the camera position that must stay fixed
    // while the azim/elev tween smoothly converges
    lookCameraPos: Vec3 | null = null;

    picker: Picker;

    mainCamera: Entity;

    // FTGS stereo split-view mode (Odyssey-style side-by-side): the main
    // camera becomes a virtual CENTER camera (still user-controlled, still
    // what controllers/picking/the FTGS depth sort see) and these two
    // derived eye cameras render the canvas halves, offset symmetrically
    // by ±splitBaseline/2 along the center camera's right axis - at 4K
    // that's two simultaneous ~2K eye views. Created lazily by
    // setSplitView(); rendered ONLY via mainPass's explicit per-camera
    // layer steps (see configureMainPassLayers()), never as self-driven
    // scene cameras.
    leftCamera: Entity | null = null;
    rightCamera: Entity | null = null;
    splitEnabled = false;
    /** distance between the two eye positions, in world units */
    splitBaseline = 0;
    /**
     * distance (world units) along the center camera's forward axis to the
     * zero-parallax plane - what shows AT the screen; nearer comes out of
     * it, farther goes behind. Since 2026-10-21 this is an off-axis
     * (asymmetric frustum) shift of each eye's projection with the eyes
     * kept parallel (see eyeProjection) - the textbook stereo setup, with
     * no vertical disparity - instead of yawing each eye inward by
     * atan((baseline/2) / this). Either way the plane sits exactly at this
     * distance wherever the camera is (unlike the reference player's
     * world-origin rotation, docs/stereo-view-design.md §2-1). 0 (the
     * default) means a PARALLEL rig - convergence at infinity. There is no
     * separate toe-in (removed 2026-10-21).
     */
    splitConvergenceDistance = 0;
    /**
     * which canvas half each eye renders into: 'LR' (default) puts the
     * left eye on the left half - the normal SBS order - and 'RL' swaps
     * them for displays that expect the crossed order.
     */
    splitSbsFormat: 'LR' | 'RL' = 'LR';

    /**
     * newest face-tracker head pose (main.ts polls
     * FtgsFaceTrackerClient.latestPose() into this every frame; null =
     * tracker off/disconnected). Applied to the CENTER camera in
     * onUpdate() - after the controller/poseOverride pose is final,
     * before the stereo eyes derive from it - so both eyes follow the
     * head together and non-split mode gets plain motion parallax.
     */
    faceTrackerPose: { fb: number, lr: number, ud: number, yaw: number, pitch: number, roll: number } | null = null;
    /**
     * where faceTrackerPose came from: 'face' = eye_tracking (reference
     * player's 0.3-scale mapping, no deadzone/clamp on the web), 'glasses' = XREAL glasses
     * IMU via xreal_eye_tracker in glasses mode - the screen is head-locked
     * there, so the head rotation is applied 1:1 (see applyFaceTrackerPose).
     */
    faceTrackerSource: 'face' | 'glasses' = 'face';
    /**
     * Head Mode for the 'face' source (2026-11-04): false = Shift (the
     * reference player's move + 0.3-scaled head rotation), true (default -
     * the Face Tracker drives LookAt as soon as it connects) = LookAt -
     * the same move, then the camera turns to keep facing the look-at
     * point (the stereo convergence point, else the orbit target), and the
     * head rotation is not used. See applyFaceTrackerPose.
     */
    faceTrackerLookAt = true;
    /**
     * world units per meter for the head-position offsets: eye_tracking
     * calibrates its output in meter-ish units, but COLMAP scene scale is
     * arbitrary - retune this per dataset (console:
     * __ftgsFaceTrackerScale), same caveat as splitBaseline.
     */
    faceTrackerTranslationScale = 1;
    /**
     * meters -> scene units for the XREAL glasses head position (anchor
     * mode, see applyFaceTrackerPose). main.ts keeps it equal to the stereo
     * metersToSceneUnits so head motion and IPD share one scale.
     */
    glassesMetersToSceneUnits = 1;
    /**
     * true (default): render each eye ANAMORPHIC - full-frame (16:9)
     * aspect squeezed into its half-width viewport. This is the standard
     * half-SBS 3D signal: an autostereoscopic display (Samsung Odyssey 3D
     * G90XF's Reality Hub SBS mode) stretches each half back to full
     * width per eye, restoring correct proportions. On a normal 2D
     * monitor this mode simply looks horizontally squeezed.
     * false: each eye uses its own half-viewport aspect - proportions are
     * correct on a normal 2D monitor (side-by-side dual-view demo), but
     * WRONG (2x stretched) after an SBS 3D display's per-eye expansion.
     */
    splitAnamorphic = true;

    mainTarget: RenderTarget;
    splatTarget: RenderTarget;
    colorTarget: RenderTarget;
    workTarget: RenderTarget;

    // pixel format of the main color target (cameraColor) - RGBA16F by
    // default, RGBA8 with ?ftgsLdr=1 or via setColorBits(8) at runtime (the
    // stats panel's Render Target toggle). See the long comment where the
    // targets are first built for why 16F is the default.
    private colorFormat: number = new URL(location.href).searchParams.has('ftgsLdr') ? PIXELFORMAT_RGBA8 : PIXELFORMAT_RGBA16F;

    // Render passes
    clearPass: RenderPass;
    mainPass: RenderPassForward;
    splatPass: RenderPassForward;
    gizmoPass: RenderPassForward;
    finalPass: SimpleRenderPass;

    // FTGS viewer mode: drop the editor-only passes from the frame. In the
    // FTGS viewer deployment the splat pass (editor's own gsplat layer) and
    // gizmo pass (overlay/gizmo layers - selection outlines, bound boxes,
    // transform gizmos) never have any content, but each still costs a full
    // render-pass begin/end with 4K attachment load/stores every frame -
    // measured via __ftgsGpuBench as part of the ~4ms non-splat baseline.
    // clearPass stays in viewer mode unless mergeClear is on: it clears the
    // color/depth buffers that mainPass renders into (splatTarget shares
    // them). See setViewerPassMode().
    viewerPassMode = false;

    // Merge Clear (viewer mode only): drop clearPass and let mainPass clear
    // its own attachments at load time. Separately, clearPass writes the
    // cleared 4K color/work/depth buffers out and mainPass immediately loads
    // color/depth back in; merged, that round trip is gone, the RGBA8 work
    // buffer (unused in viewer mode) is no longer cleared, and mainPass's
    // depth is discarded instead of stored (nothing after it reads depth -
    // finalPass blits color only). Editor mode always keeps clearPass, since
    // its splat/gizmo passes share the depth buffer. See applyClearMode().
    mergeClear = false;

    // overridden target size
    targetSizeOverride: { width: number, height: number } = null;

    // when set, overrides the tween-driven pose, fov and clipping planes each
    // update (used by 360 capture to render arbitrary face orientations that
    // the azim/elev pose system cannot express)
    poseOverride: { position: Vec3, rotation: Quat, fov: number, near: number, far: number } | null = null;

    // world transform of the user-facing camera pose. while a pose override
    // is active this holds the last tween-driven pose, so ui elements (view
    // cube, overlays) don't track the internal capture poses
    displayTransform = new Mat4();

    renderOverlays = true;

    updateCameraUniforms: () => void;

    constructor() {
        super(ElementType.camera);

        // create the camera entity
        this.mainCamera = new Entity('Camera');
        this.mainCamera.addComponent('camera');
    }

    // ortho
    set ortho(value: boolean) {
        if (value !== this.ortho) {
            this.camera.projection = value ? PROJECTION_ORTHOGRAPHIC : PROJECTION_PERSPECTIVE;
            this.scene.events.fire('camera.ortho', value);
        }
    }

    get ortho() {
        return this.camera.projection === PROJECTION_ORTHOGRAPHIC;
    }

    // fov
    set fov(value: number) {
        this.camera.fov = value;
    }

    get fov() {
        return this.camera.fov;
    }

    // tonemapping
    set tonemapping(value: string) {
        const mapping: Record<string, number> = {
            linear: TONEMAP_LINEAR,
            neutral: TONEMAP_NEUTRAL,
            aces: TONEMAP_ACES,
            aces2: TONEMAP_ACES2,
            filmic: TONEMAP_FILMIC,
            hejl: TONEMAP_HEJL
        };

        const tvalue = mapping[value];

        if (tvalue !== undefined && tvalue !== this.camera.toneMapping) {
            this.camera.toneMapping = tvalue;
            this.scene.events.fire('camera.tonemapping', value);
        }
    }

    get tonemapping() {
        switch (this.camera.toneMapping) {
            case TONEMAP_LINEAR: return 'linear';
            case TONEMAP_NEUTRAL: return 'neutral';
            case TONEMAP_ACES: return 'aces';
            case TONEMAP_ACES2: return 'aces2';
            case TONEMAP_FILMIC: return 'filmic';
            case TONEMAP_HEJL: return 'hejl';
        }
        return 'linear';
    }

    // near clip
    set near(value: number) {
        this.camera.nearClip = value;
    }

    get near() {
        return this.camera.nearClip;
    }

    // far clip
    set far(value: number) {
        this.camera.farClip = value;
    }

    get far() {
        return this.camera.farClip;
    }

    // focal point
    get focalPoint() {
        const t = this.focalPointTween.target;
        return new Vec3(t.x, t.y, t.z);
    }

    // azimuth, elevation
    get azimElev() {
        return this.azimElevTween.target;
    }

    get azim() {
        return this.azimElev.azim;
    }

    get elevation() {
        return this.azimElev.elev;
    }

    get distance() {
        return this.distanceTween.target.distance;
    }

    setFocalPoint(point: Vec3, dampingFactorFactor: number = 1) {
        this.lookCameraPos = null;
        this.focalPointTween.goto(point, dampingFactorFactor * this.scene.config.controls.dampingFactor);
    }

    // Fly mode: rotate camera around itself, keeping the camera position fixed
    look(dx: number, dy: number) {
        const sensitivity = this.scene.config.controls.orbitSensitivity;
        const d = this.distance * this.sceneRadius / this.fovFactor;

        Camera.calcForwardVec(forwardVec, this.azim, this.elevation);
        const cameraPos = this.focalPoint.add(forwardVec.clone().mulScalar(d));

        const azim = this.azim - dx * sensitivity;
        const elev = this.elevation - dy * sensitivity;

        Camera.calcForwardVec(forwardVec, azim, elev);
        const focalPoint = cameraPos.clone().sub(forwardVec.clone().mulScalar(d));

        this.setAzimElev(azim, elev);
        this.focalPointTween.goto(focalPoint, this.scene.config.controls.dampingFactor);
        this.lookCameraPos = cameraPos;
    }

    setAzimElev(azim: number, elev: number, dampingFactorFactor: number = 1) {
        // clamp
        azim = mod(azim, 360);
        elev = Math.max(this.minElev, Math.min(this.maxElev, elev));

        const t = this.azimElevTween;
        t.goto({ azim, elev }, dampingFactorFactor * this.scene.config.controls.dampingFactor);

        // handle wraparound
        if (t.source.azim - azim < -180) {
            t.source.azim += 360;
        } else if (t.source.azim - azim > 180) {
            t.source.azim -= 360;
        }

        // return to perspective mode on rotation
        this.ortho = false;
    }

    setDistance(distance: number, dampingFactorFactor: number = 1) {
        this.lookCameraPos = null;

        const controls = this.scene.config.controls;

        // clamp
        distance = Math.max(controls.minZoom, Math.min(controls.maxZoom, distance));

        const t = this.distanceTween;
        t.goto({ distance }, dampingFactorFactor * controls.dampingFactor);
    }

    setPose(position: Vec3, target: Vec3, dampingFactorFactor: number = 1) {
        vec.sub2(target, position);
        const l = vec.length();
        const azim = Math.atan2(-vec.x / l, -vec.z / l) * math.RAD_TO_DEG;
        const elev = Math.asin(vec.y / l) * math.RAD_TO_DEG;
        this.setFocalPoint(target, dampingFactorFactor);
        this.setAzimElev(azim, elev, dampingFactorFactor);
        this.setDistance(l / this.sceneRadius * this.fovFactor, dampingFactorFactor);
    }

    // set or clear the pose override and apply it immediately so subsequent
    // splat sorting and rendering see the new transform
    setPoseOverride(override: Camera['poseOverride']) {
        this.poseOverride = override;
        this.onUpdate(0);
    }

    // transform the world space coordinate to normalized screen coordinate
    worldToScreen(world: Vec3, screen: Vec3) {
        const { camera } = this;
        m.mul2(camera.projectionMatrix, camera.viewMatrix);

        v4.set(world.x, world.y, world.z, 1);
        m.transformVec4(v4, v4);

        screen.x = v4.x / v4.w * 0.5 + 0.5;
        screen.y = 1.0 - (v4.y / v4.w * 0.5 + 0.5);
        screen.z = v4.z / v4.w;
    }

    add() {
        const { camera, scene } = this;

        scene.cameraRoot.addChild(this.mainCamera);

        // configure camera to render all layers
        this.mainCamera.camera.layers = [
            scene.worldLayer.id,
            scene.splatLayer.id,
            scene.overlayLayer.id,
            scene.gizmoLayer.id
        ];

        // use manual aspect ratio mode so we can set it based on targetSize
        camera.aspectRatioMode = ASPECT_MANUAL;

        // create render passes
        const device = scene.graphicsDevice;
        const { app } = scene;
        const renderer = app.renderer;
        const composition = app.scene.layers;

        this.clearPass = new RenderPass(device);
        this.mainPass = new RenderPassForward(device, composition, app.scene, renderer);
        // RenderPassForward.frameUpdate() re-derives the clear ops from its
        // first layer step EVERY frame (updateClears), which would wipe the
        // Merge Clear setup and leave mainPass accumulating over the last
        // frame. mainPass's steps never auto-clear, so the stock version
        // only ever produced "no clears" - this does the same unless merged.
        this.mainPass.updateClears = () => this.applyMainPassClears();
        this.splatPass = new RenderPassForward(device, composition, app.scene, renderer);
        this.gizmoPass = new RenderPassForward(device, composition, app.scene, renderer);
        this.finalPass = new SimpleRenderPass(device,
            new ShaderQuad(device, vertexShader, fragmentShader, 'final-blit'), {
                vars: () => {
                    return {
                        srcTexture: this.mainTarget.colorBuffer
                    };
                }
            });

        // Name the passes explicitly. FramePass.name falls back to
        // `this.constructor.name` when unset, which terser mangles in the
        // release build - so the stats overlay's per-pass GPU split read
        // "du 2.4 / oz 0.1" instead of "main / final". Only the debug build
        // ever showed real names, and only by accident.
        this.clearPass.name = 'clear';
        this.mainPass.name = 'main';
        this.splatPass.name = 'splat';
        this.gizmoPass.name = 'gizmo';
        this.finalPass.name = 'final';

        const target = document.getElementById('canvas-container');
        this.controller = new PointerController(this, target);

        // apply scene config
        const config = scene.config;
        const controls = config.controls;

        this.minElev = (controls.minPolarAngle * 180) / Math.PI - 90;
        this.maxElev = (controls.maxPolarAngle * 180) / Math.PI - 90;

        // tonemapping
        camera.toneMapping = {
            linear: TONEMAP_LINEAR,
            filmic: TONEMAP_FILMIC,
            hejl: TONEMAP_HEJL,
            aces: TONEMAP_ACES,
            aces2: TONEMAP_ACES2,
            neutral: TONEMAP_NEUTRAL
        }[config.camera.toneMapping];

        // exposure
        scene.app.scene.exposure = config.camera.exposure;

        this.fov = config.camera.fov;

        // initial camera position and orientation
        this.setAzimElev(controls.initialAzim, controls.initialElev, 0);
        this.setDistance(controls.initialZoom, 0);

        // picker
        this.picker = new Picker(scene);

        scene.events.on('scene.boundChanged', this.onBoundChanged, this);

        // prepare camera-specific uniforms
        this.updateCameraUniforms = () => {
            const device = scene.graphicsDevice;
            const entity = this.mainCamera;
            const camera = entity.camera;

            const set = (name: string, vec: Vec3) => {
                device.scope.resolve(name).setValue([vec.x, vec.y, vec.z]);
            };

            // get frustum corners in world space
            const points = camera.camera.getFrustumCorners(-100);
            const worldTransform = this.worldTransform;
            for (let i = 0; i < points.length; i++) {
                worldTransform.transformPoint(points[i], points[i]);
            }

            // near
            if (camera.projection === PROJECTION_PERSPECTIVE) {
                // perspective
                set('near_origin', worldTransform.getTranslation());
                set('near_x', Vec3.ZERO);
                set('near_y', Vec3.ZERO);
            } else {
                // orthographic
                set('near_origin', points[3]);
                set('near_x', va.sub2(points[0], points[3]));
                set('near_y', va.sub2(points[2], points[3]));
            }

            // far
            set('far_origin', points[7]);
            set('far_x', va.sub2(points[4], points[7]));
            set('far_y', va.sub2(points[6], points[7]));
        };

        // temp control of camera start
        const url = new URL(location.href);
        const focal = url.searchParams.get('focal');
        if (focal) {
            const parts = focal.toString().split(',');
            if (parts.length === 3) {
                this.setFocalPoint(new Vec3(parseFloat(parts[0]), parseFloat(parts[1]), parseFloat(parts[2])), 0);
            }
        }
        const angles = url.searchParams.get('angles');
        if (angles) {
            const parts = angles.toString().split(',');
            if (parts.length === 2) {
                this.setAzimElev(parseFloat(parts[0]), parseFloat(parts[1]), 0);
            }
        }
        const distance = url.searchParams.get('distance');
        if (distance) {
            this.setDistance(parseFloat(distance), 0);
        }
    }

    remove() {
        const { scene } = this;

        this.controller.destroy();
        this.controller = null;

        // cleanup render passes
        this.clearPass?.destroy();
        this.mainPass?.destroy();
        this.splatPass?.destroy();
        this.gizmoPass?.destroy();
        this.finalPass?.destroy();
        this.camera.framePasses = null;

        scene.cameraRoot.removeChild(this.mainCamera);

        if (this.rightCamera) {
            this.scene.cameraRoot.removeChild(this.rightCamera);
            this.rightCamera.destroy();
            this.rightCamera = null;
        }

        this.picker.destroy();
        this.picker = null;

        scene.events.off('scene.boundChanged', this.onBoundChanged, this);
    }

    // handle the scene's bound changing. the camera must be configured to render
    // the entire extents as well as possible.
    // also update the existing camera distance to maintain the current view
    onBoundChanged(bound: BoundingBox) {
        const prevDistance = this.distanceTween.value.distance * this.sceneRadius;
        this.sceneRadius = Math.max(1e-03, bound.halfExtents.length());
        this.setDistance(prevDistance / this.sceneRadius, 0);
    }

    serialize(serializer: Serializer) {
        serializer.packa(this.worldTransform.data);
        serializer.pack(
            this.fov,
            this.tonemapping,
            this.targetSize.width,
            this.targetSize.height
        );
    }

    // handle the viewer canvas resizing
    rebuildRenderTargets() {
        const { width, height } = this.targetSize;
        const { mainTarget, scene } = this;

        // early out if size is unchanged
        if (mainTarget && mainTarget.width === width && mainTarget.height === height) {
            return;
        }

        if (!mainTarget) {
            // first time - construct render targets
            const { graphicsDevice } = scene;

            const createTexture = (name: string, width: number, height: number, format: number) => {
                return new Texture(graphicsDevice, {
                    name,
                    width,
                    height,
                    format,
                    mipmaps: false,
                    minFilter: FILTER_NEAREST,
                    magFilter: FILTER_NEAREST,
                    addressU: ADDRESS_CLAMP_TO_EDGE,
                    addressV: ADDRESS_CLAMP_TO_EDGE
                });
            };

            // RGBA16F color target (matches upstream SuperSplat). ?ftgsLdr=1
            // switches back to RGBA8; ?ftgsHdr=1 is accepted as a no-op so the
            // pre-2026-09-15 cross-check URLs keep working.
            //
            // This was RGBA8 from 2026-08-21 to 2026-09-15. Alpha blending is a
            // per-fragment read-modify-write of this attachment, so halving its
            // byte size halves ROP bandwidth on the splat draw - the measured 4K
            // bottleneck (__ftgsGpuBench: splat draw ~16ms -> ~11ms at 4K, ~43fps
            // -> ~55-60fps). That comment claimed the theoretical unorm caveats
            // (SH colors >1.0 clamping per-write, 8-bit quantization of many
            // low-alpha accumulations) "didn't manifest", on the strength of a
            // visual A/B. MEASUREMENT SAYS OTHERWISE - BOTH manifest:
            //
            //   renderer cross-check vs the C++ player (9 cameras, musical):
            //     RGBA8  PSNR 35.5-39.1 dB, mean 37.19
            //     RGBA16F PSNR 43.1-45.4 dB, mean 44.01   (+6.82 dB)
            //   camera 0 mean|A-B| by luminance band (8bit -> 16F):
            //     240-255 (0.12% of area)  29.415 -> 0.442   per-write clamp
            //      16-64  (66.9% of area)   0.830 -> 0.102   low-alpha quantization
            //
            // The visual A/B missed it because the clamp only bites on 0.12% of
            // the frame and the quantization loss is spread evenly. See
            // docs/renderer-hdr-clamp-investigation.md for how this was traced.
            //
            // THE 4K PERF COST IS REAL AND WAS NOT RE-MEASURED for this switch -
            // the numbers above still stand. If 4K playback regresses, gate this
            // on quality-tier rather than reverting outright, so the cross-check
            // keeps rendering what the reference renders.
            //
            // The final blit (blit-shader.ts) is a raw texelFetch copy into an
            // LDR backbuffer, so values >1.0 clamp once at present time - the same
            // "accumulate unclamped, clip at the end" the C++ rasterizer does.
            const colorBuffer = createTexture('cameraColor', width, height, this.colorFormat);
            const workBuffer = createTexture('workColor', width, height, PIXELFORMAT_RGBA8);
            const depthBuffer = createTexture('cameraDepth', width, height, PIXELFORMAT_DEPTH);

            // create main render target
            this.mainTarget = new RenderTarget({
                colorBuffer,
                depthBuffer,
                flipY: false,
                autoResolve: false
            });

            // create MRT render target for splat pass
            this.splatTarget = new RenderTarget({
                colorBuffers: [
                    colorBuffer,        // RT0: main color (shared)
                    workBuffer          // RT1: overlay output (shared with workTarget)
                ],
                depthBuffer,
                flipY: false,
                autoResolve: false
            });

            this.colorTarget = new RenderTarget({
                colorBuffer,
                depth: false,
                autoResolve: false
            });

            // create work buffer (used for picking, overlay output, and other operations)
            this.workTarget = new RenderTarget({
                colorBuffer: workBuffer,
                depth: false,
                autoResolve: false
            });

            // set picker render targets
            this.picker.setRenderTargets(this.colorTarget, this.workTarget);

            // clear all targets
            this.clearPass.init(this.splatTarget);
            this.clearPass.setClearColor(new Color(0, 0, 0, 0));
            this.clearPass.setClearDepth(1);
            this.clearPass.setClearStencil(0);

            // configure main pass - world layer with clears (per-camera
            // layer steps live in configureMainPassLayers so the stereo
            // split-view toggle can rebuild them at runtime)
            this.mainPass.init(this.mainTarget);
            this.configureMainPassLayers();

            // configure splat pass - MRT target, no clears
            this.splatPass.init(this.splatTarget);
            this.splatPass.addLayer(this.camera, scene.splatLayer, false, false);
            this.splatPass.addLayer(this.camera, scene.splatLayer, true, false);

            // configure gizmo pass. the gizmo layer clears depth and stencil
            // before its opaque step, after the depth-independent tool overlay
            this.gizmoPass.init(this.mainTarget);
            this.gizmoPass.addLayer(this.camera, scene.overlayLayer, false, false);
            this.gizmoPass.addLayer(this.camera, scene.overlayLayer, true, false);
            this.gizmoPass.addLayer(this.camera, scene.gizmoLayer, false, true);
            this.gizmoPass.addLayer(this.camera, scene.gizmoLayer, true, false);

            this.finalPass.init(null);

            // assign render passes to camera (honoring viewer mode if it was
            // requested before this first-time target construction ran)
            this.setViewerPassMode(this.viewerPassMode);
        } else {
            // resize existing render targets
            const { splatTarget, colorTarget, workTarget } = this;

            mainTarget.resize(width, height);
            workTarget.resize(width, height);
            colorTarget.resize(width, height);
            splatTarget.resize(width, height);
        }

        this.updateAspect();
        scene.events.fire('camera.resize', { width, height });
    }

    /** bits per channel of the main color target: 16 (RGBA16F, default) or 8 (RGBA8) */
    get colorBits(): 8 | 16 {
        return this.colorFormat === PIXELFORMAT_RGBA8 ? 8 : 16;
    }

    /**
     * Switch the main color target between RGBA16F (16) and RGBA8 (8) at
     * runtime. Only cameraColor changes format; the depth buffer and the
     * RGBA8 work buffer are reused as they are. The three render targets
     * that attach cameraColor (main / splat MRT / color) are rebuilt around
     * a new texture and every pass that renders into them is re-pointed -
     * RenderPass.init() swaps the pass target but keeps the layer render
     * steps (so the stereo/viewer-mode layer setup survives), and those
     * steps' own target references are re-pointed by hand below. init()
     * also resets the attachment ops, so clearPass's clear values are set
     * again.
     * The final blit reads mainTarget.colorBuffer each frame, and the
     * engine keys its pipeline cache on attachment formats, so both follow
     * the new texture without further work.
     */
    setColorBits(bits: 8 | 16) {
        const format = bits === 8 ? PIXELFORMAT_RGBA8 : PIXELFORMAT_RGBA16F;
        if (format === this.colorFormat) {
            return;
        }
        this.colorFormat = format;
        if (!this.mainTarget) {
            // targets not built yet - the first rebuildRenderTargets() picks it up
            return;
        }

        const { mainTarget, splatTarget, colorTarget, workTarget } = this;
        const oldColor = mainTarget.colorBuffer;
        const colorBuffer = new Texture(this.scene.graphicsDevice, {
            name: 'cameraColor',
            width: mainTarget.width,
            height: mainTarget.height,
            format,
            mipmaps: false,
            minFilter: FILTER_NEAREST,
            magFilter: FILTER_NEAREST,
            addressU: ADDRESS_CLAMP_TO_EDGE,
            addressV: ADDRESS_CLAMP_TO_EDGE
        });
        const { depthBuffer } = mainTarget;
        const workBuffer = workTarget.colorBuffer;

        this.mainTarget = new RenderTarget({
            colorBuffer,
            depthBuffer,
            flipY: false,
            autoResolve: false
        });
        this.splatTarget = new RenderTarget({
            colorBuffers: [colorBuffer, workBuffer],
            depthBuffer,
            flipY: false,
            autoResolve: false
        });
        this.colorTarget = new RenderTarget({
            colorBuffer,
            depth: false,
            autoResolve: false
        });

        this.picker.setRenderTargets(this.colorTarget, this.workTarget);

        this.clearPass.init(this.splatTarget);
        this.clearPass.setClearColor(new Color(0, 0, 0, 0));
        this.clearPass.setClearDepth(1);
        this.clearPass.setClearStencil(0);
        this.mainPass.init(this.mainTarget);
        this.splatPass.init(this.splatTarget);
        this.gizmoPass.init(this.mainTarget);

        // init() does NOT re-point the existing layer render steps: each
        // LayerRenderStep captured its pass's target when addLayer() made
        // it, and the renderer sizes the viewport from THAT target
        // (renderForwardLayer -> setupViewport). Left alone they keep the
        // destroyed old target, which rebuildRenderTargets() never resizes
        // - so after the next canvas resize (e.g. windowed -> fullscreen)
        // the scene drew into an old-size viewport in one corner of the
        // new target, the rest black. Re-point every step at its pass's
        // new target.
        for (const pass of [this.mainPass, this.splatPass, this.gizmoPass]) {
            for (const step of pass.layerRenderSteps) {
                step.renderTarget = pass.renderTarget;
            }
        }

        // mainPass.init() above reset its attachment ops - restore the
        // Merge Clear setup
        this.applyClearMode();

        // the old targets only wrap textures - destroying them frees their
        // framebuffers, not the shared depth/work textures, which live on
        mainTarget.destroy();
        splatTarget.destroy();
        colorTarget.destroy();
        oldColor.destroy();

        this.scene.forceRender = true;
    }

    // (re)build mainPass's layer render steps: the main (center) camera
    // alone in normal mode; the two derived eye cameras - and NOT the
    // center camera - when split view is active. Each eye camera's
    // normalized viewport rect confines its draw to its half of the
    // shared render target (the engine's setupViewport honors camera.rect
    // per layer step).
    private configureMainPassLayers() {
        const { scene } = this;
        if (!this.mainPass) {
            // add() hasn't constructed the passes yet - the first
            // rebuildRenderTargets() call re-applies via this method
            return;
        }
        this.mainPass.layerRenderSteps.length = 0;
        if (this.splitEnabled && this.leftCamera && this.rightCamera) {
            this.mainPass.addLayer(this.leftCamera.camera, scene.worldLayer, false, false);
            this.mainPass.addLayer(this.leftCamera.camera, scene.worldLayer, true, false);
            this.mainPass.addLayer(this.rightCamera.camera, scene.worldLayer, false, false);
            this.mainPass.addLayer(this.rightCamera.camera, scene.worldLayer, true, false);
        } else {
            this.mainPass.addLayer(this.camera, scene.worldLayer, false, false);
            this.mainPass.addLayer(this.camera, scene.worldLayer, true, false);
        }
    }

    // split view aspect: anamorphic mode keeps the FULL-frame aspect per
    // eye (squeezed into the half viewport - the SBS 3D display's per-eye
    // 2x horizontal expansion restores it); dual-view mode uses the
    // per-eye half-viewport aspect so proportions read correctly on an
    // ordinary 2D monitor. See the splitAnamorphic field doc.
    private updateAspect() {
        const { width, height } = this.targetSize;
        if (!width || !height) {
            return;
        }
        const aspectWidth = (this.splitEnabled && !this.splitAnamorphic) ? width / 2 : width;
        this.camera.horizontalFov = aspectWidth > height;
        this.camera.aspectRatio = aspectWidth / height;
        for (const eye of [this.leftCamera, this.rightCamera]) {
            if (eye) {
                eye.camera.horizontalFov = this.camera.horizontalFov;
                eye.camera.aspectRatio = this.camera.aspectRatio;
            }
        }
    }

    // lazily create the two derived eye cameras. Never self-driven scene
    // cameras: disabled + layerless keeps the engine's own layer
    // composition from scheduling extra full renders to the backbuffer -
    // these cameras only ever draw through mainPass's explicit layer
    // steps, which reference the components directly regardless of
    // enabled state.
    private ensureEyeCameras() {
        // side: -1 = left eye (at -baseline/2 along the center's right
        // axis), +1 = right eye
        const makeEye = (name: string, side: number) => {
            const entity = new Entity(name);
            entity.addComponent('camera');
            const c = entity.camera;
            c.enabled = false;
            c.layers = [];
            c.aspectRatioMode = ASPECT_MANUAL;
            // the off-axis projection (see eyeProjection) - the engine calls
            // this for the shader uniforms and the culling frustum alike
            c.calculateProjection = (mat: Mat4) => this.eyeProjection(mat, c, side);
            this.scene.cameraRoot.addChild(entity);
            return entity;
        };
        if (!this.leftCamera) {
            this.leftCamera = makeEye('LeftEyeCamera', -1);
        }
        if (!this.rightCamera) {
            this.rightCamera = makeEye('RightEyeCamera', 1);
        }
    }

    /**
     * Parallel-axis, off-axis ("asymmetric frustum") stereo projection for
     * one eye (2026-10-21 - replaces the old per-eye toe-in rotation): both
     * eyes look exactly along the center camera's forward axis, and each
     * frustum is shifted horizontally so that the plane at
     * splitConvergenceDistance lands on the same screen spot in both eyes -
     * that plane shows at zero parallax, nearer points in front of the
     * screen, farther ones behind it. Unlike rotating the eyes inward, the
     * two image planes stay coplanar, so there is no vertical disparity
     * (keystone) towards the image edges and the horizontal parallax is
     * exactly focal * baseline * (1/depth - 1/convergence) everywhere.
     *
     * Built from scratch on every call (the engine hands back the same
     * matrix each frame), as the engine's own perspective matrix plus the
     * shift: NDC x += k * w, k = side * (baseline/2) / convergence * P[0].
     * A convergence of 0 means a parallel rig - no shift.
     */
    private eyeProjection(mat: Mat4, c: CameraComponent, side: number) {
        if (c.projection !== PROJECTION_PERSPECTIVE) {
            const y = c.orthoHeight;
            const x = y * c.aspectRatio;
            mat.setOrtho(-x, x, -y, y, c.nearClip, c.farClip);
            return;
        }
        mat.setPerspective(c.fov, c.aspectRatio, c.nearClip, c.farClip, c.horizontalFov);
        if (this.splitConvergenceDistance <= 0) {
            return;
        }
        const d = mat.data;
        const k = side * (this.splitBaseline / 2) / this.splitConvergenceDistance * d[0];
        // row 0 += k * row 3 (row 3 yields clip w = view-space depth)
        d[0] += k * d[3];
        d[4] += k * d[7];
        d[8] += k * d[11];
        d[12] += k * d[15];
    }

    // assign each eye camera its canvas half per splitSbsFormat
    private applySplitRects() {
        if (!this.leftCamera || !this.rightCamera) {
            return;
        }
        const leftOnLeft = this.splitSbsFormat !== 'RL';
        this.leftCamera.camera.rect = new Vec4(leftOnLeft ? 0 : 0.5, 0, 0.5, 1);
        this.rightCamera.camera.rect = new Vec4(leftOnLeft ? 0.5 : 0, 0, 0.5, 1);
    }

    /**
     * Toggle Odyssey-style side-by-side stereo: ON splits the canvas into
     * exact left/right halves rendered simultaneously from the two derived
     * eye cameras (the main camera becomes a virtual center camera - see
     * the leftCamera/rightCamera field doc); OFF restores the ordinary
     * single full-width center view.
     */
    setSplitView(enable: boolean, baseline?: number) {
        if (baseline !== undefined) {
            this.splitBaseline = baseline;
        }
        if (this.splitEnabled === enable) {
            return;
        }
        this.splitEnabled = enable;

        if (enable) {
            this.ensureEyeCameras();
            this.applySplitRects();
        }

        // the main (center) camera keeps the full-width rect in both
        // modes: in split mode it contributes no mainPass layer steps at
        // all (the eye cameras own them - see configureMainPassLayers)
        this.camera.rect = new Vec4(0, 0, 1, 1);
        this.configureMainPassLayers();
        this.updateAspect();
        this.scene.forceRender = true;
    }

    /** swap which canvas half each eye renders into ('LR' = left eye on the left half) */
    setSbsFormat(format: 'LR' | 'RL') {
        if (this.splitSbsFormat === format) {
            return;
        }
        this.splitSbsFormat = format;
        this.applySplitRects();
        this.scene.forceRender = true;
    }

    /**
     * Switch between the full editor frame (clear/main/splat/gizmo/final)
     * and the slimmed FTGS-viewer frame (clear/main/final) - see the
     * viewerPassMode field doc. Safe to call before the passes exist (first
     * canvas resize constructs them); the stored flag is honored then.
     */
    setViewerPassMode(enable: boolean) {
        this.viewerPassMode = enable;
        if (!this.clearPass) {
            // add() hasn't constructed the passes yet - rebuildRenderTargets()
            // re-applies the stored flag once they exist
            return;
        }
        this.applyClearMode();
    }

    /**
     * Toggle Merge Clear (see the mergeClear field doc). Only takes effect
     * in viewer mode; the stored flag is honored whenever viewer mode is
     * (re)entered.
     */
    setMergeClear(enable: boolean) {
        this.mergeClear = enable;
        if (this.clearPass) {
            this.applyClearMode();
        }
    }

    // assign the frame passes and mainPass's attachment ops for the current
    // viewerPassMode/mergeClear pair. Must re-run after every mainPass.init(),
    // which resets the ops (rebuildRenderTargets' first build, setColorBits).
    private applyClearMode() {
        const { mainPass } = this;

        // the passes exist from add(), but their attachment ops only from
        // the first rebuildRenderTargets()'s init() - which calls
        // setViewerPassMode() again, so the merged setup is applied then
        const opsReady = !!mainPass.depthStencilOps;
        const merged = opsReady && this.viewerPassMode && this.mergeClear;

        if (opsReady) {
            this.applyMainPassClears();
            // init() default is store; discard only when merged (viewer mode)
            mainPass.depthStencilOps.storeDepth = !merged;
        }

        if (merged) {
            this.camera.framePasses = [mainPass, this.finalPass];
        } else if (this.viewerPassMode) {
            this.camera.framePasses = [this.clearPass, mainPass, this.finalPass];
        } else {
            this.camera.framePasses = [this.clearPass, mainPass, this.splatPass, this.gizmoPass, this.finalPass];
        }
    }

    // mainPass's clear ops - also its per-frame updateClears() (see the
    // override where mainPass is constructed)
    private applyMainPassClears() {
        const { mainPass } = this;
        if (this.viewerPassMode && this.mergeClear) {
            mainPass.setClearColor(MERGED_CLEAR_COLOR);
            mainPass.setClearDepth(1);
            mainPass.setClearStencil(0);
        } else {
            mainPass.setClearColor(undefined);
            mainPass.setClearDepth(undefined);
            mainPass.setClearStencil(undefined);
        }
    }

    onUpdate(deltaTime: number) {
        // controller update
        this.controller.update(deltaTime);

        // update underlying values
        this.focalPointTween.update(deltaTime);
        this.azimElevTween.update(deltaTime);
        this.distanceTween.update(deltaTime);

        const azimElev = this.azimElevTween.value;
        const distance = this.distanceTween.value;

        Camera.calcForwardVec(forwardVec, azimElev.azim, azimElev.elev);

        if (this.lookCameraPos) {
            cameraPosition.copy(this.lookCameraPos);
            if (this.azimElevTween.timer >= this.azimElevTween.transitionTime) {
                this.lookCameraPos = null;
            }
        } else {
            cameraPosition.copy(forwardVec);
            cameraPosition.mulScalar(distance.distance * this.sceneRadius / this.fovFactor);
            cameraPosition.add(this.focalPointTween.value);
        }

        if (this.poseOverride) {
            // cameraRoot has identity transform, so local space is world space
            const { position, rotation, fov, near, far } = this.poseOverride;
            this.mainCamera.setLocalPosition(position);
            this.mainCamera.setLocalRotation(rotation);
            this.camera.fov = fov;
            this.near = near;
            this.far = far;
        } else {
            this.mainCamera.setLocalPosition(cameraPosition);
            this.mainCamera.setLocalEulerAngles(azimElev.elev, azimElev.azim, 0);

            this.fitClippingPlanes(this.mainCamera.getLocalPosition(), this.mainCamera.forward);

            this.displayTransform.copy(this.mainCamera.getWorldTransform());
        }

        // FaceTracker head pose (docs/stereo-view-design.md §5-3): applied
        // to the center camera after the controller/poseOverride pose is
        // final (and after displayTransform was captured, so serialized
        // poses stay head-free). Like the stereo eye derivation below,
        // this mutation never accumulates - the top of every onUpdate
        // re-derives the pose from the controller/poseOverride.
        if (this.faceTrackerPose) {
            this.applyFaceTrackerPose(this.faceTrackerPose);
        }

        const { camera } = this.mainCamera;
        const { targetSize } = this;

        // update ortho height
        camera.orthoHeight = this.distanceTween.value.distance * this.sceneRadius / this.fovFactor * (this.fov / 90) * (camera.horizontalFov ? targetSize.height / targetSize.width : 1);
        camera.camera._updateViewProjMat();

        // stereo split view: derive both eye cameras symmetrically from
        // the main camera, which acts as the virtual CENTER of the rig -
        // ±baseline/2 along its right axis, oriented exactly like it
        // (parallel axes; convergence is a projection shift, not a
        // rotation - see eyeProjection). The main camera itself is left
        // untouched (it renders nothing in split mode but keeps the center
        // pose, so controllers/picking/the FTGS depth sort all keep seeing
        // the center view). cameraRoot has identity transform, so local
        // space is world space (same assumption the poseOverride path
        // above relies on).
        if (this.splitEnabled && this.leftCamera && this.rightCamera) {
            const halfBaseline = this.splitBaseline / 2;
            const centerRot = this.mainCamera.getLocalRotation();
            const centerPos = this.mainCamera.getLocalPosition();
            const eyes: [Entity, number][] = [[this.leftCamera, -1], [this.rightCamera, 1]];
            for (const [eye, side] of eyes) {
                eye.setLocalRotation(centerRot);
                vec.copy(this.mainCamera.right).mulScalar(side * halfBaseline).add(centerPos);
                eye.setLocalPosition(vec);

                const ec = eye.camera;
                ec.projection = camera.projection;
                ec.fov = camera.fov;
                ec.nearClip = camera.nearClip;
                ec.farClip = camera.farClip;
                ec.toneMapping = camera.toneMapping;
                ec.gammaCorrection = camera.gammaCorrection;
                ec.orthoHeight = camera.orthoHeight;
                ec.camera._updateViewProjMat();
            }
        }
    }

    // Port of the reference player's apply_facetracker_transform
    // (gaussian_renderer.cpp): a camera-local translation and a local
    // Rz→Ry→Rx rotation. Scales are verbatim from the reference; its per-axis
    // deadzone (위치 0.015·앞뒤 0.025·각도 1°) and clamp (위치 0.35·앞뒤 0.5·각도 180°)
    // 는 2026-11-02 사용자 요청으로 웹에서 없앰 - 받은 값을 그대로 씀. The axis
    // SIGNS are converted from its COLMAP-style camera frame (+Z forward,
    // Y down) into PlayCanvas's GL frame (-Z forward, Y up), i.e. y/z
    // translation and yaw/roll rotation flip. If a live axis still feels
    // inverted, fix it in eye_tracking's per-axis scale calibration
    // (face_tracking_settings.json allows negative scales) rather than
    // here, so both viewers stay consistent.
    private applyFaceTrackerPose(pose: NonNullable<Camera['faceTrackerPose']>) {
        if (this.faceTrackerSource === 'glasses') {
            // XREAL glasses, no deadzone/clamp/0.3 scale, since the image
            // must stay fixed in the world as the head moves. The tool sends
            // one of two kinds of packet:
            // - normal mode: real head rotation in degrees, position 0 -
            //   same axis signs and Rz->Ry->Rx order as below
            // - anchor mode (the glasses pin the screen in the room
            //   themselves): rotation 0, head position in meters (fb =
            //   forward, lr = right, ud = up), applied 1:1 as a camera-local
            //   move, so the screen acts as a window into the scene
            const s = this.glassesMetersToSceneUnits;
            this.mainCamera.translateLocal(pose.lr * s, pose.ud * s, -pose.fb * s);
            this.mainCamera.rotateLocal(0, 0, -pose.roll);
            this.mainCamera.rotateLocal(0, pose.yaw, 0);
            this.mainCamera.rotateLocal(-pose.pitch, 0, 0);
            return;
        }
        const { fb, lr, ud, yaw, pitch, roll } = pose;
        const ts = this.faceTrackerTranslationScale;
        if (this.faceTrackerLookAt && this.applyFaceTrackerLookAt(-lr * 0.9 * ts, -ud * 0.9 * ts, -fb * 1.5 * ts)) {
            return;
        }
        this.mainCamera.translateLocal(-lr * 0.9 * ts, -ud * 0.9 * ts, -fb * 1.5 * ts);
        // local-frame rotation, applied in the reference's Rz→Ry→Rx order
        // (three separate rotateLocal calls = successive local-space
        // rotations, matching c2w · Rz · Ry · Rx)
        this.mainCamera.rotateLocal(0, 0, -roll * 0.3);
        this.mainCamera.rotateLocal(0, yaw * 0.3, 0);
        this.mainCamera.rotateLocal(-pitch * 0.3, 0, 0);
    }

    // Head Mode LookAt (2026-11-04): move the center camera by the head
    // offset (camera-local x/y/z of the base pose, the Shift mode's mapping),
    // then turn it to face T = P0 + F0 * D, the point the base pose looks
    // at - the stereo convergence point when there is one (it then stays at
    // the screen centre with zero parallax in both eyes), else the orbit
    // target. Up stays the base pose's up, so a tilted view gains no roll.
    // Re-derived from the base pose every frame like the rest of onUpdate,
    // so nothing accumulates, and a zero offset reproduces the base pose
    // exactly. The head rotation is deliberately unused: the direction is
    // the look-at's. Returns false (caller falls back to Shift) when there
    // is no usable look-at distance.
    private applyFaceTrackerLookAt(x: number, y: number, z: number) {
        const cam = this.mainCamera;
        const p0 = cam.getLocalPosition();
        const d = this.splitEnabled && this.splitConvergenceDistance > 0 ?
            this.splitConvergenceDistance :
            vec.sub2(this.focalPointTween.value as Vec3, p0).length();
        if (!(d > 1e-6)) {
            return false;
        }
        lookAtForward.copy(cam.forward);
        lookAtUp.copy(cam.up);
        lookAtTarget.copy(lookAtForward).mulScalar(d).add(p0);

        cam.translateLocal(x, y, z);

        // safety only (not a motion limit): keep the camera at least 10% of
        // D in front of T along the base forward, or a head pushed past T
        // would flip the view around
        const p = cam.getLocalPosition();
        const ahead = vec.sub2(lookAtTarget, p).dot(lookAtForward);
        if (ahead < 0.1 * d) {
            vec.copy(lookAtForward).mulScalar(ahead - 0.1 * d).add(p);
            cam.setLocalPosition(vec);
        }
        cam.lookAt(lookAtTarget, lookAtUp);
        return true;
    }

    fitClippingPlanes(cameraPosition: Vec3, forwardVec: Vec3) {
        const bound = this.scene.bound;
        const boundRadius = bound.halfExtents.length();

        vec.sub2(bound.center, cameraPosition);
        const dist = vec.dot(forwardVec);

        if (dist > 0) {
            this.far = dist + boundRadius;
            // if camera is placed inside the sphere bound calculate near based far
            this.near = Math.max(1e-6, dist < boundRadius ? this.far / (1024 * 16) : dist - boundRadius);
        } else {
            // if the scene is behind the camera
            this.far = boundRadius * 2;
            this.near = this.far / (1024 * 16);
        }
    }

    onPreRender() {
        this.rebuildRenderTargets();
        this.updateCameraUniforms();
    }

    onPostRender() {

    }

    focus(options?: { focalPoint: Vec3, radius: number, speed: number }) {
        const getSplatFocalPoint = () => {
            for (const element of this.scene.elements) {
                if (element.type === ElementType.splat) {
                    const focalPoint = (element as Splat).focalPoint?.();
                    if (focalPoint) {
                        return focalPoint;
                    }
                }
            }
        };

        const focalPoint = options ? options.focalPoint : (getSplatFocalPoint() ?? this.scene.bound.center);
        const focalRadius = options ? options.radius : this.scene.bound.halfExtents.length();

        const fdist = focalRadius / this.sceneRadius;

        this.setDistance(isFinite(fdist) ? fdist : 1, options?.speed ?? 0);
        this.setFocalPoint(focalPoint, options?.speed ?? 0);
    }

    get fovFactor() {
        // use the larger axis fov (which is always this.fov) so camera distance
        // stays constant regardless of viewport aspect ratio.
        return Math.sin(this.fov * math.DEG_TO_RAD * 0.5);
    }

    getRay(screenX: number, screenY: number, ray: Ray) {
        const { camera, ortho } = this;
        const cameraPos = this.mainCamera.getPosition();

        // create the pick ray in world space
        if (ortho) {
            camera.screenToWorld(screenX, screenY, -1.0, vec);
            camera.screenToWorld(screenX, screenY, 1.0, vecb);
            vecb.sub(vec).normalize();
            ray.set(vec, vecb);
        } else {
            camera.screenToWorld(screenX, screenY, 1.0, vec);
            vec.sub(cameraPos).normalize();
            ray.set(cameraPos, vec);
        }
    }

    // intersect the scene at the given normalized screen coordinate (0-1 range) using depth picking
    async intersect(x: number, y: number) {
        const { scene } = this;
        const splats = scene.getElementsByType(ElementType.splat);

        let closestDepth = Infinity;
        let closestSplat: Splat | null = null;

        // Find the splat with the smallest depth at this screen position
        for (let i = 0; i < splats.length; ++i) {
            const splat = splats[i] as Splat;

            this.picker.prepareDepth(splat);
            const normalizedDepth = await this.picker.readDepth(x, y);

            if (normalizedDepth !== null && normalizedDepth < closestDepth) {
                closestDepth = normalizedDepth;
                closestSplat = splat;
            }
        }

        if (!closestSplat) {
            return null;
        }

        // Convert normalized depth to linear depth
        const linearDepth = closestDepth * (this.far - this.near) + this.near;

        // Convert normalized coordinates to screen pixels for getRay
        const screenX = x * scene.canvas.clientWidth;
        const screenY = y * scene.canvas.clientHeight;

        // Calculate world position from ray and depth
        this.getRay(screenX, screenY, ray);
        const t = linearDepth / ray.direction.dot(this.mainCamera.forward);
        const position = new Vec3();
        position.copy(ray.origin).add(vec.copy(ray.direction).mulScalar(t));

        return {
            splat: closestSplat,
            position: position,
            distance: t
        };
    }

    // intersect the scene at the normalized screen location (0-1 range) and focus the camera on this location
    async pickFocalPoint(x: number, y: number) {
        const result = await this.intersect(x, y);
        if (result) {
            const { scene } = this;

            this.setFocalPoint(result.position);
            this.setDistance(result.distance / this.sceneRadius * this.fovFactor);
            scene.events.fire('camera.focalPointPicked', {
                camera: this,
                splat: result.splat,
                position: result.position
            });
        }
    }

    // pick mode

    // render picker contents
    pickPrep(splat: Splat, mode: 'add' | 'remove' | 'set' | 'intersect') {
        this.picker.prepareId(splat, mode);
    }

    pick(x: number, y: number) {
        return this.picker.readId(x, y);
    }

    pickRect(x: number, y: number, width: number, height: number) {
        return this.picker.readIds(x, y, width, height);
    }

    docSerialize() {
        const pack3 = (v: Vec3) => [v.x, v.y, v.z];

        return {
            focalPoint: pack3(this.focalPointTween.target),
            azim: this.azim,
            elev: this.elevation,
            distance: this.distance,
            fov: this.fov,
            tonemapping: this.tonemapping
        };
    }

    docDeserialize(settings: any) {
        this.setFocalPoint(new Vec3(settings.focalPoint), 0);
        this.setAzimElev(settings.azim, settings.elev, 0);
        this.setDistance(settings.distance, 0);
        this.fov = settings.fov;
        this.tonemapping = settings.tonemapping;
    }

    // offscreen render mode

    startOffscreenMode(width: number, height: number) {
        this.targetSizeOverride = { width, height };
        this.finalPass.enabled = false;
        this.rebuildRenderTargets();
        this.onUpdate(0);
    }

    endOffscreenMode() {
        this.targetSizeOverride = null;
        this.finalPass.enabled = true;
        this.rebuildRenderTargets();
        this.onUpdate(0);
    }

    get targetSize() {
        return this.targetSizeOverride ?? this.scene.targetSize;
    }

    get camera() {
        return this.mainCamera.camera;
    }

    get worldTransform() {
        return this.mainCamera.getWorldTransform();
    }

    get position() {
        return this.mainCamera.getPosition();
    }

    get forward() {
        return this.mainCamera.forward;
    }
}

export { Camera };
